/**
 * @file PostHog wiring and instrumentation tuning.
 *
 * The project API key is a *write-only* ingest token — PostHog ships it in
 * every public browser snippet on purpose — so it lives here as a literal
 * rather than behind a build secret. That keeps CI secret-free and, more
 * importantly, means a deploy can never silently ship with analytics switched
 * off because an environment variable went missing. `VITE_POSTHOG_KEY` still
 * overrides it when a fork or a scratch project wants its own bucket.
 *
 * Volume, not privacy, is the constraint that shapes everything below: this is
 * a 60 fps game, so a per-bullet or per-kill event would be millions of rows a
 * day and would tell you less than one well-shaped summary per level. The rule
 * the bridge follows is: *discrete decisions* become events, *repeated actions*
 * become counters on the level and run summaries.
 */

/** @type {string} Project API key. Public by design; see the file header. */
const KEY = 'phc_kuZrWZS2j2MpAXhfv39nkTEsroNyoy5ySf6qt8PhqPko';

/**
 * Instrumentation settings.
 * @readonly
 */
export const ANALYTICS = Object.freeze({
  /** Project API key, env override first. */
  key: (import.meta.env?.VITE_POSTHOG_KEY || KEY).trim(),
  /**
   * Ingest endpoint — a first-party domain, not `us.i.posthog.com`.
   *
   * `z.chainsawteam.com` is PostHog's *managed* reverse proxy: a CNAME to
   * `30dd9b246b227bcdb4b6.cf-prod-us-proxy.proxyhog.com` and nothing of our own
   * to run or renew. It exists because the common blocklists drop requests to
   * `*.posthog.com` outright, which silently loses the analytics of the players
   * most likely to be running an ad blocker.
   *
   * The record must stay **DNS-only** in Cloudflare. Orange-clouding it puts our
   * proxy in front of theirs and breaks the Cloudflare-for-SaaS certificate,
   * which surfaces as `ERR_SSL_VERSION_OR_CIPHER_MISMATCH` — no analytics at
   * all rather than degraded analytics. Verify with
   * `curl -I https://z.chainsawteam.com/flags/?v=2` (expect 200).
   *
   * The proxy also fronts `/static/*` and `/array/*`, so the SDK's own
   * extensions (exception autocapture, web vitals) load first-party too.
   */
  host: 'https://z.chainsawteam.com',
  /** App host, so the in-app toolbar links resolve. Never the proxy. */
  uiHost: 'https://us.posthog.com',
  /**
   * Master switch. `VITE_POSTHOG_DISABLED=1` turns instrumentation off for a
   * build without editing code — useful for a local perf capture where the
   * network tab has to stay quiet.
   */
  enabled: import.meta.env?.VITE_POSTHOG_DISABLED !== '1',
  /** Verbose SDK logging. On in dev so a missing event is visible in console. */
  debug: Boolean(import.meta.env?.DEV),
  /** Seconds between `performance_sampled` heartbeats while playing. */
  performanceSampleSeconds: 60,
  /**
   * Floor between two `player_damaged` events. The player can be hit several
   * times a second; the summary counters carry the true totals, so this only
   * has to be dense enough to show *where* damage comes from.
   */
  playerDamageMinIntervalSeconds: 1,
  /** Seconds between gamepad probes, until one is seen. */
  gamepadProbeSeconds: 2
});

/**
 * Which deployment this build is. Derived from Vite's base path, which the
 * Worker's `BASE_PATH` already distinguishes: `/frenzy/` is production and
 * `/frenzy/<branch>/` is a PR preview.
 *
 * @returns {'development' | 'preview' | 'production'}
 */
export function analyticsEnvironment() {
  if (import.meta.env?.DEV) return 'development';
  const base = String(import.meta.env?.BASE_URL ?? '/');
  // Segment count, not string matching: `/frenzy/` is 1, `/frenzy/my-branch/` is 2.
  const segments = base.split('/').filter(Boolean);
  return segments.length > 1 ? 'preview' : 'production';
}

/**
 * @returns {boolean} True when this build has a usable key and is switched on.
 */
export function analyticsConfigured() {
  return ANALYTICS.enabled && ANALYTICS.key.startsWith('phc_');
}
